// @author Jayden Scahill - Old Mate Developments

import { createClient } from '@supabase/supabase-js';

// Use proxy URL in development, direct URL in production
const supabaseUrl = import.meta.env.DEV 
  ? `${window.location.origin}/supabase`
  : import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error('Missing Supabase environment variables');
}

export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
    detectSessionInUrl: true,
    storage: localStorage,
    storageKey: 'omtm-auth'
  },
  global: {
    headers: {
      'x-application-name': 'omtm'
    }
  },
  // Add request timeout
  realtime: {
    timeout: 30000 // 30 seconds
  }
});

// Retry configuration - increased for better resilience
const RETRY_COUNT = 15; // Increased from 10 to 15
const RETRY_DELAY = 2000; // 2 seconds
const REQUEST_TIMEOUT = 900000; // 15 minutes (increased from 5 minutes)

const sleep = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

// Enhanced error message generation
function generateErrorMessage(error: any): string {
  // Handle network/connection errors first
  if (error.message?.includes('socket hang up') ||
      error.message?.includes('ECONNRESET') ||
      error.message?.includes('ENOTFOUND') ||
      error.message?.includes('ECONNREFUSED') ||
      error.message?.includes('network error') ||
      error.message?.includes('Network Error') ||
      error.message?.includes('ERR_NETWORK') ||
      error.message?.includes('ERR_INTERNET_DISCONNECTED') ||
      error.message?.includes('Failed to fetch') ||
      error.code === 'NETWORK_ERROR' || 
      error.code === 'CONNECTION_ERROR' ||
      error.code === 'ECONNRESET' ||
      error.code === 'ENOTFOUND' ||
      error.code === 'ECONNREFUSED') {
    return 'Connection lost. Please check your internet connection and try again.';
  }

  // Handle timeout errors
  if (error.message?.includes('timeout') || error.message?.includes('Request timed out')) {
    return 'The request took too long to complete. Please try again.';
  }

  // Handle authentication errors
  if (error.code === 'refresh_token_not_found' || 
      error.message?.includes('refresh_token_not_found') ||
      error.message?.includes('Invalid Refresh Token')) {
    return 'Your session has expired. Please sign in again.';
  }

  // Handle specific database error codes
  if (error.code === '23505') {
    return 'A record with this unique identifier already exists';
  }
  if (error.code === 'PGRST204' || error.code === 'PGRST116') {
    return 'The requested data could not be found';
  }
  if (error.code === '42501') {
    return 'You do not have permission to perform this action';
  }

  // Try to extract meaningful error message in order of preference
  let errorMessage = '';

  // 1. Try error.message first (if it's not empty)
  if (error.message && error.message.trim() !== '') {
    errorMessage = error.message;
  }
  // 2. Try error.details if message is empty
  else if (error.details && error.details.trim() !== '') {
    errorMessage = `Database error: ${error.details}`;
  }
  // 3. Try error.hint if both message and details are empty
  else if (error.hint && error.hint.trim() !== '') {
    errorMessage = `Hint: ${error.hint}`;
  }
  // 4. Try error.code if available
  else if (error.code) {
    errorMessage = `Database error (code: ${error.code})`;
  }
  // 5. Check if it's a PostgreSQL error with more specific info
  else if (error.severity || error.routine) {
    const parts = [];
    if (error.severity) parts.push(`Severity: ${error.severity}`);
    if (error.routine) parts.push(`Routine: ${error.routine}`);
    errorMessage = `Database error - ${parts.join(', ')}`;
  }
  // 6. Last resort - provide a generic but helpful message
  else {
    errorMessage = 'An unexpected database error occurred. Please try again or contact support if the problem persists.';
  }

  return errorMessage;
}

// Error handling wrapper with retry logic and timeout
export async function handleSupabaseError<T>(
  promise: Promise<{ data: T | null; error: any }>,
  options: { allowNoData?: boolean; retries?: number; timeout?: number } = {}
): Promise<T | null> {
  const retries = options.retries ?? RETRY_COUNT;
  const timeout = options.timeout ?? REQUEST_TIMEOUT;
  let lastError: any;

  // Create timeout promise
  const timeoutPromise = new Promise((_, reject) => {
    setTimeout(() => reject(new Error('Request timed out')), timeout);
  });

  for (let attempt = 0; attempt < retries; attempt++) {
    try {
      // Race between the request and timeout
      const { data, error } = await Promise.race([
        promise,
        timeoutPromise
      ]) as { data: T | null; error: any };
      
      if (error) {
        // Network errors should trigger a retry
        if (error.message?.includes('Failed to fetch') ||
            error.message?.includes('ECONNRESET') ||
            error.message?.includes('ENOTFOUND') ||
            error.message?.includes('ECONNREFUSED') ||
            error.message?.includes('network error') ||
            error.message?.includes('Network Error') ||
            error.message?.includes('ERR_NETWORK') ||
            error.message?.includes('ERR_INTERNET_DISCONNECTED') ||
            error.message?.includes('socket hang up') ||
            error.code === 'NETWORK_ERROR' || 
            error.code === 'CONNECTION_ERROR' ||
            error.code === 'ECONNRESET' ||
            error.code === 'ENOTFOUND' ||
            error.code === 'ECONNREFUSED' ||
            error.message?.includes('timeout')) {
          lastError = error;
          console.warn(`Network error attempt ${attempt + 1}/${retries}, retrying...`);
          await sleep(RETRY_DELAY * Math.pow(2, attempt)); // Exponential backoff
          continue;
        }

        // Use enhanced error message generation
        const errorMessage = generateErrorMessage(error);
        throw new Error(errorMessage);
      }
      
      // For DELETE operations or when explicitly allowed, null data is valid
      if (!data && !options.allowNoData) {
        throw new Error('No data returned');
      }
      
      return data;
    } catch (error: any) {
      lastError = error;
      
      // Only retry on network errors or timeouts
      if (!error.message?.includes('Failed to fetch') &&
          !error.message?.includes('ECONNRESET') &&
          !error.message?.includes('ENOTFOUND') &&
          !error.message?.includes('ECONNREFUSED') &&
          !error.message?.includes('network error') &&
          !error.message?.includes('Network Error') &&
          !error.message?.includes('ERR_NETWORK') &&
          !error.message?.includes('ERR_INTERNET_DISCONNECTED') &&
          !error.message?.includes('socket hang up') &&
          error.code !== 'NETWORK_ERROR' && 
          error.code !== 'CONNECTION_ERROR' &&
          error.code !== 'ECONNRESET' &&
          error.code !== 'ENOTFOUND' &&
          error.code !== 'ECONNREFUSED' &&
          !error.message?.includes('timeout') &&
          !error.message?.includes('Request timed out')) {
        throw error;
      }
      
      if (attempt === retries - 1) {
        console.error('Supabase error after all retries:', error);
        throw new Error('Unable to connect to the server after multiple attempts. Please check your internet connection and try again.');
      }
      
      await sleep(RETRY_DELAY * Math.pow(2, attempt)); // Exponential backoff
    }
  }

  throw lastError;
}

// Function to delete a customer and all related data
export async function deleteCustomerWithCascade(customerId: string) {
  try {
    // First verify the customer exists and user has permission
    const { data: customer, error: customerError } = await supabase
      .from('customers')
      .select('id')
      .eq('id', customerId)
      .maybeSingle();

    if (customerError) {
      if (customerError.code === '42501') {
        throw new Error('You do not have permission to delete this customer');
      }
      throw new Error(customerError.message);
    }

    if (!customer) {
      throw new Error('Customer not found');
    }

    // Call the delete_customer_cascade function
    const { error: deleteError } = await supabase.rpc('delete_customer_cascade', {
      p_customer_id: customerId
    });

    if (deleteError) {
      if (deleteError.code === '42501') {
        throw new Error('You do not have permission to delete this customer');
      }
      if (deleteError.message?.includes('invalid transaction termination')) {
        throw new Error('Failed to delete customer due to a database transaction error. Please try again.');
      }
      throw new Error(`Failed to delete customer: ${deleteError.message}`);
    }

    // Verify deletion was successful
    const { data: verifyCustomer, error: verifyError } = await supabase
      .from('customers')
      .select('id')
      .eq('id', customerId)
      .maybeSingle();

    if (verifyError && !verifyError.message?.includes('Results contain 0 rows')) {
      throw new Error('Failed to verify customer deletion');
    }

    if (verifyCustomer) {
      throw new Error('Customer deletion failed - customer still exists');
    }

    return true;
  } catch (error: any) {
    console.error('Error in deleteCustomerWithCascade:', error);
    throw error; // Preserve the original error message
  }
}